import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { get } from 'data/services/config';
import { isPendingAction, isRejectedAction } from './common';
import { Communication } from './communicationsTypes';

// Initial state
const initialState: {
  loading: boolean;
  byEventId: Record<string, Communication[]>;
} = {
  loading: false,
  byEventId: {},
};

// Actions
export const fetchCommunications = createAsyncThunk(
  'communications/fetch',
  async ({ eventId }: { eventId: string }, { rejectWithValue }) => {
    try {
      return await get(`/events/${eventId}/communications`);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const communicationsSlice = createSlice({
  name: 'communicationsSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommunications.fulfilled, (state, action) => {
        const byEventId = Object.groupBy(
          action.payload.communications as Communication[],
          (communication) => String(communication.event_id),
        ) as Record<string, Communication[]>;

        state.byEventId = { ...state.byEventId, ...byEventId };
        state.loading = false;
      })
      .addMatcher(isPendingAction('communications'), (state) => {
        state.loading = true;
      })
      .addMatcher(isRejectedAction('communications'), (state) => {
        state.loading = false;
      });
  },
  selectors: {
    getIsLoading: (state) => state.loading,
    getCommunications: (state, eventId: string, phaseId?: string) => {
      // First gather all communications that are not associated with an event or phase
      const defaultCommunications = state.byEventId['null'] || [];
      const eventCommunications = state.byEventId[eventId] || [];
      const phaseCommunications = phaseId
        ? eventCommunications.filter((communication) => communication.phase_id === Number(phaseId))
        : [];

      // Then, for each communication, find the one that is associated with the event or phase if it exists
      // If it doesn't exist, return the default communication
      return defaultCommunications.map((communication) => {
        const byPhaseCommunication = phaseCommunications.find((c) => c.id === communication.id);
        const byEventCommunication = eventCommunications.find((c) => c.id === communication.id);
        return byPhaseCommunication ?? byEventCommunication ?? communication;
      });
    },
    getCommunicationsBySlug: (state, slug: string, eventId: string, phaseId?: string) => {
      return communicationsSlice
        .getSelectors()
        .getCommunications(state, eventId, phaseId)
        .find((communication) => communication.slug === slug);
    },
  },
});

export const fromCommunications = communicationsSlice.selectors;
export default communicationsSlice.reducer;
